import type { ReactNode } from 'react';
import './Numeric.css';

export interface MonoProps {
  children: ReactNode;
  /**
   * What kind of literal this is. Purely for the accessible name — it does not
   * change the visual treatment — but it matters: a screen reader announcing
   * "REC-01824" letter-by-letter is useless without knowing it is a record id.
   * Omit for figures inside a labelled cell, where the column header already
   * carries the meaning and a repeated label is noise.
   */
  label?: string;
  className?: string;
}

/**
 * A monospaced literal value — record ids, cluster ids, hashes, source paths,
 * error codes, action-run ids (design-system catalogue, Tier 1).
 *
 * Exists because of design-fidelity audit gap F-9: the prototype uses
 * `IBM Plex Mono` in 115 places to mark exactly this class of value, and the
 * build used it twice, so identifiers rendered as ordinary prose. That is a
 * semantic loss, not only a visual one — mono is how the design says "this is a
 * literal you might copy, compare character by character, or paste into a
 * support ticket".
 *
 * Deliberately NOT for:
 * - Keyboard keys — that is `Kbd`, which is a `<kbd>` element with different
 *   semantics and its own border treatment.
 * - Money — that is `Money`, which needs currency-aware `Intl` formatting and
 *   stays in the sans face with tabular figures, exactly as the prototype does.
 * - Prose that merely contains a number. Mono on a sentence is noise.
 */
export function Mono({ children, label, className }: MonoProps) {
  return (
    <span
      className={className ? `dpr-mono ${className}` : 'dpr-mono'}
      // `aria-label` replaces the announced text; the visible characters stay
      // identical, so this adds the noun without hiding the value.
      {...(label ? { 'aria-label': `${label}: ${String(children)}` } : {})}
    >
      {children}
    </span>
  );
}
